import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { getLastResults, getDetailResults } from "../../../../apiservices/CasionApi";
import RefResultModal from "../../../casinoComponents/refResultModal";
import Lucky7ResultBody from "../../../casinoComponents/refResultModal/Lucky7ResultBody";
import { formatRefMatchTime } from "../../../../utils/refMatchTime";

/**
 * Last Result items for Lucky 7 (variant "lucky7") and Amar Akbar Anthony (variant "aaa") in the
 * reference markup — rendered inside `.casino-video-last-results`:
 *   lucky7: win 1 → <span class="resultlow">L</span>, 2 → resulthigh "H", 0 → resulttie "T"
 *   aaa:    win 1 → resulta "A", 2 → resultb "B", 3 → resultc "C", 0 → resulttie "T"
 * (each span keeps the reference's " " separators between its conditional letters).
 * Click → full-page .loader-overlay while the round detail loads, then the reference result modal.
 */
const LETTERS = {
  lucky7: [
    ["1", "L", "resultlow"],
    ["2", "H", "resulthigh"],
    ["0", "T", "resulttie"],
  ],
  aaa: [
    ["1", "A", "resulta"],
    ["2", "B", "resultb"],
    ["3", "C", "resultc"],
    ["0", "T", "resulttie"],
  ],
};

export default function Lucky7RefResults({ gameType, data = [], variant = "lucky7", fallbackTitle = "" }) {
  const apiType = (gameType || "lucky7").toLowerCase().trim();
  const letters = LETTERS[variant] || LETTERS.lucky7;
  const [lastResults, setLastResults] = useState([]);
  const [detailResult, setDetailResult] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchLastResults = async () => {
      try {
        const response = await getLastResults(apiType);
        if (response?.data?.data?.res) {
          setLastResults(response.data.data.res.map((item) => ({ mid: item.mid, win: String(item.win) })));
        }
      } catch (error) {
        console.error("Error fetching last results:", error);
      }
    };
    fetchLastResults();
    const interval = setInterval(fetchLastResults, 5000);
    return () => clearInterval(interval);
  }, [apiType]);

  const handleResultClick = async (item) => {
    if (!item?.mid || loading) return;
    setLoading(true);
    try {
      const response = await getDetailResults(apiType, item.mid.toString());
      const t1 = response?.data?.data?.t1 ?? response?.data?.t1;
      if (t1) {
        setDetailResult(t1);
        setShowModal(true);
      }
    } catch (error) {
      console.error("Error fetching detail results:", error);
    }
    setLoading(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setDetailResult(null);
  };

  // API results; before they arrive, the feed's lrs ({mid, win} or a bare win) as a fallback
  const items =
    lastResults.length > 0
      ? lastResults
      : (Array.isArray(data) ? data : []).map((r) =>
          typeof r === "object" && r ? { mid: r.mid, win: String(r.win) } : { mid: null, win: String(r) }
        );

  return (
    <>
      {items.map((item, index) => {
        const cls = letters.find(([w]) => w === item.win)?.[2];
        return (
          <span key={item.mid || index} className={cls} onClick={() => handleResultClick(item)}>
            {letters.map(([w, letter], i) => (
              <React.Fragment key={w}>
                {i > 0 && " "}
                {item.win === w ? letter : null}
              </React.Fragment>
            ))}
          </span>
        );
      })}
      {loading && createPortal(<div className="loader-overlay" />, document.querySelector(".page-content") || document.body)}
      <RefResultModal
        show={showModal && !loading && !!detailResult}
        onHide={handleCloseModal}
        title={`${detailResult?.ename || fallbackTitle} Result`}
        roundId={detailResult?.rid}
        matchTime={formatRefMatchTime(detailResult?.mtime)}
      >
        <Lucky7ResultBody t1={detailResult} variant={variant} />
      </RefResultModal>
    </>
  );
}
